import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
} from '@angular/core';
import { AbilityServiceSignal } from '@casl/angular';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { RoleAction, RolePermissionSubject } from '@wilson/interfaces';
import { isWithinFileSizeLimit } from '@wilson/utils';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzUploadFile, NzUploadModule } from 'ng-zorro-antd/upload';

@Component({
  selector: 'wilson-training-qualifications-bulk-edit-attachments-section',
  imports: [
    CommonModule,
    NzButtonModule,
    NzCollapseModule,
    NzIconModule,
    NzTooltipModule,
    NzUploadModule,
    TranslatePipe,
  ],
  templateUrl:
    './training-qualifications-bulk-edit-attachments-section.component.html',
  styleUrl:
    './training-qualifications-bulk-edit-attachments-section.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrainingQualificationsBulkEditAttachmentsSectionComponent {
  private readonly nzMessageService = inject(NzMessageService);
  private readonly translateService = inject(TranslateService);

  @Input() active: boolean | null = true;
  @Input() disabled: boolean | null = false;
  @Input() disabledNzUpload: boolean | null = false;
  @Input() disabledFileList: boolean | null = false;
  @Input() customHeader!: string;
  @Input() customInfo!: string;
  @Input() multipleTrainingsSelected: boolean | null = false;

  @Input() fileList: NzUploadFile[] = [];
  @Output() fileListChange = new EventEmitter<NzUploadFile[]>();

  protected readonly maxFileSizeInBytes: number = 5 * 1024 * 1024 + 1;
  public can = inject(AbilityServiceSignal).can;
  public readonly RoleAction = RoleAction;
  public readonly RolePermissionSubject = RolePermissionSubject;

  public beforeUpload = (file: NzUploadFile): boolean => {
    const allowedMaxFileSizeInMB = 5;
    const isValidFileSize = isWithinFileSizeLimit(
      file.size,
      allowedMaxFileSizeInMB,
    );
    if (!isValidFileSize) {
      this.showFileToBigErrorMessage(file);
    } else {
      this.addFileToFileList(file);
    }
    return false;
  };

  private showFileToBigErrorMessage(file: NzUploadFile): void {
    this.nzMessageService.error(
      `${file.name ?? file.filename ?? ''} ${this.translateService.instant(
        'user_qualifications.attachments.upload.error.file_to_big',
      )}`,
    );
  }

  private addFileToFileList(file: NzUploadFile): void {
    this.fileList = this.fileList.concat(file);
    this.fileListChange.emit(this.fileList);
  }
}
